import {
  proposedConsignmentFeeScheduleIsValid,
  type ProposedConsignmentFeeSchedule,
} from './consignment-terms';

/** Whole-price payout tiers, rounded to nearest cent (half up). Never deduct marketplace fees again. */
export interface ConsignmentFeePolicy extends Omit<
  ProposedConsignmentFeeSchedule,
  'kind' | 'selectedItemType' | 'selectedService' | 'zeroProceedsFloor'
> {
  kind: 'FULL_SCHEDULE_V1';
  selectedItemType: 'SINGLE_CARD' | 'SEALED_WAX';
  selectedService: 'STANDARD' | 'EXPRESS' | 'PREMIUM';
  zeroProceedsFloor: true;
  unsoldFeeMode: 'ADD_LISTING_AND_SERVICE';
  refundPolicy: 'ORIGINAL_TIER_REVERSE_FULL' | 'RETIER_REVERSE_FULL';
}
export interface ConsignmentFeeComponents {
  commissionAmountMinor: number;
  listingFeeMinor: number;
  serviceFeeMinor: number;
}
export interface ConsignmentFeeCalculation {
  schemaVersion: 1;
  originalGrossMinor: number;
  refundAmountMinor: number;
  netGrossAmountMinor: number;
  payoutBps: number;
  commissionBps: number;
  scheduled: ConsignmentFeeComponents;
  applied: ConsignmentFeeComponents;
  waived: ConsignmentFeeComponents;
  commissionAmountMinor: number;
  fixedFeeMinor: number;
  estimatedProceedsMinor: number;
}
export function consignmentFeePolicyIsValid(value: unknown): value is ConsignmentFeePolicy {
  if (!value || typeof value !== 'object') return false;
  const p = value as ConsignmentFeePolicy;
  return (
    p.kind === 'FULL_SCHEDULE_V1' &&
    proposedConsignmentFeeScheduleIsValid({ ...p, kind: 'PROPOSED_FULL_SCHEDULE_V1' }) &&
    ['SINGLE_CARD', 'SEALED_WAX'].includes(p.selectedItemType) &&
    ['STANDARD', 'EXPRESS', 'PREMIUM'].includes(p.selectedService) &&
    p.zeroProceedsFloor === true &&
    p.unsoldFeeMode === 'ADD_LISTING_AND_SERVICE' &&
    ['ORIGINAL_TIER_REVERSE_FULL', 'RETIER_REVERSE_FULL'].includes(p.refundPolicy)
  );
}
const money = (n: number) => Number.isSafeInteger(n) && n >= 0;
function validate(p: ConsignmentFeePolicy): void {
  if (!consignmentFeePolicyIsValid(p)) throw new Error('Invalid consignment fee policy');
}
function fixed(p: ConsignmentFeePolicy) {
  return {
    listingFeeMinor:
      p.selectedItemType === 'SEALED_WAX' ? p.soldSealedWaxFeeMinor : p.soldSingleCardFeeMinor,
    serviceFeeMinor:
      p.selectedService === 'EXPRESS'
        ? p.expressSurchargeMinor
        : p.selectedService === 'PREMIUM'
          ? p.premiumSurchargeMinor
          : 0,
  };
}
export function evaluateConsignmentUnsoldFees(p: ConsignmentFeePolicy): {
  unsoldFeeMinor: number;
  listingFeeMinor: number;
  serviceFeeMinor: number;
  amountMinor: number;
} {
  validate(p);
  const fees = fixed(p);
  return {
    ...fees,
    unsoldFeeMinor: p.unsoldItemFeeMinor,
    amountMinor: p.unsoldItemFeeMinor + fees.listingFeeMinor + fees.serviceFeeMinor,
  };
}
/** Apply commission, then listing, then service; waive unfunded amounts in reverse order. Income balances cash. */
export function evaluateConsignmentSaleFees(
  p: ConsignmentFeePolicy,
  originalGrossMinor: number,
  refundMinor = 0,
  reversed = false,
): ConsignmentFeeCalculation {
  validate(p);
  if (!money(originalGrossMinor) || !money(refundMinor) || refundMinor > originalGrossMinor)
    throw new Error('Invalid sale/refund amount');
  const originalTier = [...p.tiers]
    .reverse()
    .find((t) => t.minimumGrossMinor <= originalGrossMinor);
  if (!originalTier) throw new Error('Sale price is below the agreed fee schedule');
  const netGrossAmountMinor = reversed ? 0 : originalGrossMinor - refundMinor;
  const tier =
    p.refundPolicy === 'RETIER_REVERSE_FULL' && netGrossAmountMinor > 0
      ? [...p.tiers].reverse().find((t) => t.minimumGrossMinor <= netGrossAmountMinor)
      : originalTier;
  if (!tier) throw new Error('Refund price is below the agreed fee schedule');
  const zero = { commissionAmountMinor: 0, listingFeeMinor: 0, serviceFeeMinor: 0 };
  const payout = Number((BigInt(netGrossAmountMinor) * BigInt(tier.payoutBps) + 5000n) / 10000n);
  const scheduled =
    netGrossAmountMinor === 0
      ? { ...zero }
      : { commissionAmountMinor: netGrossAmountMinor - payout, ...fixed(p) };
  let available = netGrossAmountMinor;
  const applied = { ...zero };
  const waived = { ...zero };
  for (const key of ['commissionAmountMinor', 'listingFeeMinor', 'serviceFeeMinor'] as const) {
    applied[key] = Math.min(available, scheduled[key]);
    available -= applied[key];
    waived[key] = scheduled[key] - applied[key];
  }
  return {
    schemaVersion: 1,
    originalGrossMinor,
    refundAmountMinor: reversed ? originalGrossMinor : refundMinor,
    netGrossAmountMinor,
    payoutBps: tier.payoutBps,
    commissionBps: 10000 - tier.payoutBps,
    scheduled,
    applied,
    waived,
    commissionAmountMinor: applied.commissionAmountMinor,
    fixedFeeMinor: applied.listingFeeMinor + applied.serviceFeeMinor,
    estimatedProceedsMinor: available,
  };
}
export function consignmentFeeCalculationMatches(
  actual: unknown,
  expected: ConsignmentFeeCalculation,
): boolean {
  if (!actual || typeof actual !== 'object') return false;
  const a = actual as ConsignmentFeeCalculation;
  return (
    (
      [
        'schemaVersion',
        'originalGrossMinor',
        'refundAmountMinor',
        'netGrossAmountMinor',
        'payoutBps',
        'commissionBps',
        'commissionAmountMinor',
        'fixedFeeMinor',
        'estimatedProceedsMinor',
      ] as const
    ).every((k) => a[k] === expected[k]) &&
    (['scheduled', 'applied', 'waived'] as const).every(
      (group) =>
        a[group] &&
        (['commissionAmountMinor', 'listingFeeMinor', 'serviceFeeMinor'] as const).every(
          (k) => a[group][k] === expected[group][k],
        ),
    )
  );
}
